import {isSpot, isTask, useTaskScheduleDelete} from '@domain/shifthandover'
import {useEventDelete} from '@hconnect/common/hooks/useEventDelete'
import {ShiftEvent} from '@hconnect/common/types'
import {usePermission} from '@hproduce/permissions'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'

import {ConfirmationDialog, ErrorDialog} from './dialogs'

type Props = {
  event: ShiftEvent
  showDialog: boolean
  plantId: string
  country: string
  hasChecklists?: boolean
  setDialogOpen: (state: boolean) => void
  onDelete: (event: ShiftEvent) => void
  onlyDeleteSeries?: boolean
}

export const EventDelete: React.FC<Props> = ({
  event,
  onDelete,
  showDialog,
  setDialogOpen,
  onlyDeleteSeries,
  plantId,
  country,
  hasChecklists
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const plantContext = {plantId, countryId: country}
  const eventDeleteAction = useEventDelete(plantId, {
    onSuccess: () => onDelete(event)
  })
  const seriesDeleteAction = useTaskScheduleDelete({
    plantId,
    options: {
      onSuccess: () => onDelete(event)
    }
  })

  const canDeletePlantTasks = usePermission({plantContext, permissionTypes: ['DELETE_PLANT_TASKS']})
  const canDeletePlantEvents = usePermission({
    plantContext,
    permissionTypes: ['DELETE_PLANT_EVENTS']
  })

  const isAllowed = isTask(event) ? canDeletePlantTasks : canDeletePlantEvents

  if (!isAllowed || !event.id || isSpot(event)) return null

  const isSeriesItem = !!event?.['repetitionInfo']

  return (
    <>
      {eventDeleteAction.error ? (
        <ErrorDialog
          httpCode={eventDeleteAction.error.code}
          statusCode={eventDeleteAction.error?.response?.status}
          onClose={() => {
            setDialogOpen(false)
            eventDeleteAction.reset()
          }}
          errorText={eventDeleteAction.error.message}
        />
      ) : seriesDeleteAction.error ? (
        <ErrorDialog
          httpCode={seriesDeleteAction.error.code}
          statusCode={seriesDeleteAction.error?.response?.status}
          onClose={() => {
            setDialogOpen(false)
            seriesDeleteAction.reset()
          }}
          errorText={seriesDeleteAction.error.message}
        />
      ) : (
        // show error or confirm dialog but not both
        showDialog && (
          <ConfirmationDialog
            onDeleteSingle={() => {
              eventDeleteAction.mutate(event.id)
            }}
            onDeleteSeries={() => {
              seriesDeleteAction.mutate(event.id)
            }}
            onlyDeleteSeries={onlyDeleteSeries}
            onClose={() => setDialogOpen(false)}
            isSeriesItem={isSeriesItem}
            isSingleLoading={eventDeleteAction.isPending}
            isSeriesLoading={seriesDeleteAction.isPending}
            title={event.title ?? ''}
            additionalMessage={hasChecklists ? t('shiftEvent.deleteWithChecklistWarning') : ''}
          />
        )
      )}
    </>
  )
}
